import React, { useMemo, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { getMedicationPdf } from '../../services/api';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import { needsScreeningAck } from './needsScreeningAck';
import { AttestationDialog } from '../signing/AttestationDialog';
import type { ESignatureCredentialInput } from '../signing/esignatureCredentialApi';
import InteractionScreenPanel from './InteractionScreenPanel';
import {
  selectAttestationForFamily,
  type SigningAttestation,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import RadioButtonFormSection from './RadioButtonFormSection';
import {
  MEDICATION_FORM_QUESTIONS,
  MEDICATION_REVIEW_DISCLAIMER,
  YesNoNaValue,
  YesNoValue,
} from './medicationFormConstants';

interface MedicationRecordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  onDocumentCreated?: () => void;
}

const MedicationRecordModal: React.FC<MedicationRecordModalProps> = ({
  open,
  onOpenChange,
  patientId,
  onDocumentCreated,
}) => {
  const currentUser = useStore(state => state.user);
  const { error: showError } = useToast();
  const { attestations, isLoading: attestationsLoading } =
    useActiveAttestations();
  const medicationAttestation: SigningAttestation | null = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(attestations, 'medication');
  }, [attestations]);
  const [physicianContacted, setPhysicianContacted] = useState<
    YesNoNaValue | ''
  >('');
  const [physicianContactedExplanation, setPhysicianContactedExplanation] =
    useState('');

  const [actionTaken, setActionTaken] = useState<YesNoValue | ''>('');
  const [actionTakenExplanation, setActionTakenExplanation] = useState('');

  const [interactionsNotified, setInteractionsNotified] = useState<
    YesNoNaValue | ''
  >('');
  const [interactionsNotifiedExplanation, setInteractionsNotifiedExplanation] =
    useState('');

  const [screeningUnavailableAck, setScreeningUnavailableAck] = useState(false);
  const [forceScreeningUnavailable, setForceScreeningUnavailable] =
    useState(false);

  const [attestationOpen, setAttestationOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleRequestGenerate = () => {
    if (!medicationAttestation) return;
    setAttestationOpen(true);
  };

  const closeAndReset = () => {
    onOpenChange(false);
    setAttestationOpen(false);
    // Reset form
    setPhysicianContacted('');
    setPhysicianContactedExplanation('');
    setActionTaken('');
    setActionTakenExplanation('');
    setInteractionsNotified('');
    setInteractionsNotifiedExplanation('');
    setScreeningUnavailableAck(false);
    setForceScreeningUnavailable(false);
  };

  // Returns a message when generation fails so the attestation dialog, which
  // stays open on failure, can show it beside the password field the clinician
  // has to retype. Closing on failure would discard what they just typed and
  // make them reopen the dialog for every retry.
  const doGenerate = async (
    credential: ESignatureCredentialInput
  ): Promise<string | null> => {
    if (!medicationAttestation) return null;
    if (
      physicianContacted === '' ||
      actionTaken === '' ||
      interactionsNotified === ''
    ) {
      return null;
    }
    setIsGenerating(true);

    try {
      const response = await getMedicationPdf({
        path: { patient_id: patientId },
        body: {
          ...credential,
          attestation_version: medicationAttestation.version,
          physician_contacted: physicianContacted,
          physician_contacted_explanation: physicianContactedExplanation,
          action_taken: actionTaken,
          action_taken_explanation: actionTakenExplanation,
          interactions_notified: interactionsNotified,
          interactions_notified_explanation: interactionsNotifiedExplanation,
          screening_unavailable_acknowledged: screeningUnavailableAck,
        },
      });

      if (response.error) {
        throw response;
      }

      downloadResponseAsFile(response, 'medication_record.pdf');

      // Close modal and reset form
      closeAndReset();
      onDocumentCreated?.();
      return null;
    } catch (error) {
      console.error('Failed to generate medication record:', error);
      // The sign endpoint returns 422 when screening is unavailable and the
      // clinician has not acknowledged it. Surface the acknowledgment path
      // instead of a generic toast so they can sign (see needsScreeningAck).
      // This is the one failure the clinician cannot fix inside the attestation
      // dialog, so it closes and the toast points at the checkbox behind it.
      if (await needsScreeningAck(error)) {
        setForceScreeningUnavailable(true);
        setAttestationOpen(false);
        showError('Acknowledge that screening was unavailable to sign.');
        return null;
      }
      // A mistyped e-signature password is now the likeliest failure here, and
      // each silent retry burns one of the five attempts before lockout, so
      // the server's message has to reach the clinician.
      // Async variant because this endpoint responds with a PDF blob, so the
      // error body has to be read out of the blob before it can be surfaced.
      return await getUserFacingApiMessageAsync(
        error,
        'Failed to generate medication record. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      closeAndReset();
    }
  };

  const isFormValid =
    physicianContacted !== '' &&
    actionTaken !== '' &&
    interactionsNotified !== '';

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Generate Patient Medication Record</DialogTitle>
            <DialogDescription>
              Please complete the following information before generating the
              medication record.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-6 py-4'>
            {/* Physician Contacted */}
            <RadioButtonFormSection
              label={MEDICATION_FORM_QUESTIONS.PHYSICIAN_CONTACTED.label}
              htmlFor='physician-contacted'
              value={physicianContacted}
              onChange={val => setPhysicianContacted(val as YesNoNaValue)}
              options={MEDICATION_FORM_QUESTIONS.PHYSICIAN_CONTACTED.options}
              explanationValue={physicianContactedExplanation}
              onExplanationChange={setPhysicianContactedExplanation}
            />

            {/* Action Taken */}
            <RadioButtonFormSection
              label={MEDICATION_FORM_QUESTIONS.ACTION_TAKEN.label}
              htmlFor='action-taken'
              value={actionTaken}
              onChange={val => setActionTaken(val as YesNoValue)}
              options={MEDICATION_FORM_QUESTIONS.ACTION_TAKEN.options}
              explanationValue={actionTakenExplanation}
              onExplanationChange={setActionTakenExplanation}
            />

            {/* Interactions Notified */}
            <RadioButtonFormSection
              label={MEDICATION_FORM_QUESTIONS.INTERACTIONS_NOTIFIED.label}
              htmlFor='interactions-notified'
              value={interactionsNotified}
              onChange={val => setInteractionsNotified(val as YesNoNaValue)}
              options={MEDICATION_FORM_QUESTIONS.INTERACTIONS_NOTIFIED.options}
              explanationValue={interactionsNotifiedExplanation}
              onExplanationChange={setInteractionsNotifiedExplanation}
            />

            {/* Interaction screening (drug-drug / drug-allergy) */}
            <InteractionScreenPanel
              patientId={patientId}
              acknowledged={screeningUnavailableAck}
              onAcknowledgedChange={setScreeningUnavailableAck}
              forceUnavailable={forceScreeningUnavailable}
            />

            {/* Review acknowledgement (legal attestation is confirmed in the next step) */}
            <div className='border-t pt-4 space-y-3'>
              <p className='text-sm text-muted-foreground'>
                {MEDICATION_REVIEW_DISCLAIMER}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={handleClose}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestGenerate}
              disabled={
                !isFormValid ||
                isGenerating ||
                attestationsLoading ||
                !medicationAttestation
              }
            >
              {isGenerating && (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              )}
              <FileText className='h-4 w-4 mr-2' />
              Sign and Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttestationDialog
        open={attestationOpen}
        attestation={medicationAttestation}
        signerName={currentUser?.name ?? null}
        signerEmail={currentUser?.email ?? null}
        isLoading={isGenerating}
        actionLabel='Sign'
        onConfirm={doGenerate}
        onCancel={() => setAttestationOpen(false)}
      />
    </>
  );
};

export default MedicationRecordModal;
